import { doc, getDoc, setDoc } from 'firebase/firestore';
import type { User } from 'firebase/auth';
import { db } from './firebase';
import { defaultCmpConfigForRegion, Region } from './defaults';

const TENANT_STORAGE_KEY = 'cmp_active_tenant';

export function rememberActiveTenant(tenantId: string) {
  window.localStorage.setItem(TENANT_STORAGE_KEY, tenantId);
}

export function getActiveTenant(): string | null {
  return window.localStorage.getItem(TENANT_STORAGE_KEY);
}

export function clearActiveTenant() {
  window.localStorage.removeItem(TENANT_STORAGE_KEY);
}

function slugifyDomain(domain: string): string {
  return domain
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/\/.*$/, '')
    .replace(/[^a-z0-9-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60);
}

function randomSuffix(): string {
  const bytes = new Uint8Array(4);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(36)).join('').slice(0, 6);
}

export async function generateTenantId(domain: string): Promise<string> {
  const base = slugifyDomain(domain) || 'tenant';
  for (let attempt = 0; attempt < 5; attempt++) {
    const candidate = `${base}-${randomSuffix()}`;
    const snap = await getDoc(doc(db, 'companies', candidate));
    if (!snap.exists()) return candidate;
  }
  return `${base}-${Date.now().toString(36)}`;
}

export type OnboardingInput = {
  user: User;
  domain: string;
  region: Region;
  forWhom: 'self' | 'client' | 'multiple';
  domainCount: '1' | '2-5' | '6-25' | '25+';
  companyDomainsTotal: '1-5' | '6-25' | '26-100' | '100+';
  branding?: {
    theme?: 'auto' | 'light' | 'dark';
    accentColor?: string;
    logoUrl?: string | null;
  };
};

export async function createTenant(input: OnboardingInput): Promise<{ tenantId: string }> {
  // Force a fresh ID token so email_verified=true (set during email-link sign-in)
  // is present in the claims Firestore rules evaluate. Without this, the first
  // write right after sign-in can fail with permission-denied because the
  // in-memory token predates the verified flag.
  try {
    await input.user.getIdToken(true);
    await input.user.reload();
  } catch (err) {
    console.warn('[createTenant] token refresh failed', err);
  }

  const tenantId = await generateTenantId(input.domain);
  const now = new Date().toISOString();

  const companyDoc = {
    tenantId,
    name: input.domain,
    primaryDomain: input.domain,
    plan: 'free',
    createdAt: now,
    createdBy: input.user.uid,
    forWhom: input.forWhom,
    domainCount: input.domainCount,
    companyDomainsTotal: input.companyDomainsTotal,
    region: input.region,
    branding: input.branding ?? {},
    domains: [extractDomainHost(input.domain)],
  };

  const userDoc = {
    uid: input.user.uid,
    email: input.user.email,
    role: 'companyAdmin' as const,
    joinedAt: now,
  };

  const cmpConfig = defaultCmpConfigForRegion(input.region, input.domain);

  // Three separate writes so we can pinpoint which one trips the rules.
  // Firestore rules check each op against the pre-batch state, so cmpConfig
  // (which requires isCompanyAdmin) must happen AFTER the users doc that
  // grants the role exists.
  try {
    await setDoc(doc(db, 'companies', tenantId), companyDoc);
  } catch (err) {
    const tagged = err instanceof Error ? err : new Error(String(err));
    tagged.message = `step 1 (companies/${tenantId}): ${tagged.message}`;
    throw tagged;
  }

  try {
    await setDoc(doc(db, 'companies', tenantId, 'users', input.user.uid), userDoc);
  } catch (err) {
    const tagged = err instanceof Error ? err : new Error(String(err));
    tagged.message = `step 2 (companies/${tenantId}/users/${input.user.uid}): ${tagged.message}`;
    throw tagged;
  }

  try {
    await setDoc(doc(db, 'companies', tenantId, 'cmpConfig', 'current'), cmpConfig);
  } catch (err) {
    const tagged = err instanceof Error ? err : new Error(String(err));
    tagged.message = `step 3 (companies/${tenantId}/cmpConfig/current): ${tagged.message}`;
    throw tagged;
  }

  rememberActiveTenant(tenantId);
  return { tenantId };
}

function extractDomainHost(url: string): string {
  try {
    const u = new URL(url.startsWith('http') ? url : `https://${url}`);
    return u.hostname;
  } catch {
    return url;
  }
}

export async function setBrandingForTenant(
  tenantId: string,
  branding: { theme?: 'auto' | 'light' | 'dark'; accentColor?: string; logoUrl?: string | null },
): Promise<void> {
  await setDoc(
    doc(db, 'companies', tenantId),
    { branding, updatedAt: new Date().toISOString() },
    { merge: true },
  );
  await setDoc(
    doc(db, 'companies', tenantId, 'cmpConfig', 'current'),
    {
      theme: brandingToTheme(branding),
      updatedAt: new Date().toISOString(),
    },
    { merge: true },
  );
}

function brandingToTheme(branding: { accentColor?: string }) {
  if (!branding.accentColor) return undefined;
  return { '--cmp-brand': branding.accentColor };
}

export async function getTenant(tenantId: string) {
  const snap = await getDoc(doc(db, 'companies', tenantId));
  return snap.exists() ? snap.data() : null;
}
